import React, { useCallback, useMemo, useState } from "react";

import {
  Box,
  Button,
  HStack,
  IconButton,
  useDisclosure
} from "@chakra-ui/react";

import { MdOutlineLinkOff } from "react-icons/md";
import { RiSettings3Line } from "react-icons/ri";
import { useDispatch } from "react-redux";

import { DataType } from "@/jenesys-types/data/enums";
import { Client } from "@/jenesys-types/jenesys-entities/config";
import { EntityRouteListIds, ListSearchOperator } from "@/jenesys-types/list";

import { ConfirmModal } from "@/components/common/controls/ConfirmModal";
import { CrudList } from "@/components/common/crud/CrudList/CrudList";
import { ColumnDefinition } from "@/components/common/table/types";
import { AddClient } from "@/components/features/client/AddClient";
import useEntityDelete from "@/hooks/crud/useEntityDelete";
import useNamedNavigate from "@/hooks/navigation/useNamedNavigate";
import { refreshList } from "@/store/crud/list";
import { changeClient } from "@/store/features/companies/companies";
import { toastSuccess } from "@/util/helpers";

const searchFields = ["company_name"];

export const ManageClientsListRoute: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNamedNavigate();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const {
    isOpen: isAddClientOpen,
    onOpen: onAddClientOpen,
    onClose: onAddClientClose
  } = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const { deleteItem } = useEntityDelete(DataType.Clients);

  const goToClientSettings = useCallback(
    (client: Client) => {
      dispatch(changeClient(client.id));
      navigate("settings.client", { clientId: client.id });
    },
    [dispatch, navigate]
  );

  const goToClient = useCallback(
    (client: Client) => {
      dispatch(changeClient(client.id));
      navigate("clientdashboard", { clientId: client.id });
    },
    [dispatch, navigate]
  );

  const handleUnlinkClient = useCallback(
    (client: Client) => {
      deleteItem(client);
      dispatch(refreshList(DataType.Clients, EntityRouteListIds.ManageClients));
      deleteDisclosure.onClose();
      setSelectedClient(null);
      toastSuccess("Client unlinked successfully.");
    },
    [deleteItem, dispatch, deleteDisclosure]
  );

  const columns = useMemo<Array<ColumnDefinition<Client>>>(
    () => [
      { id: "CompanyName", header: "Company Name", accessor: "name" },
      {
        id: "AccountingPlatform",
        header: "Accounting Platform",
        accessor: "accounting_platform"
      },
      {
        id: "ACTION",
        header: "ACTION",
        accessor: (client) => (
          <HStack>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedClient(client);
                deleteDisclosure.onOpen();
              }}
              variant="default"
              colorScheme="default"
              h={5}
              aria-label="Unlink"
              fontSize={20}
              color="primary.500"
              icon={<MdOutlineLinkOff />}
            />

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                goToClientSettings(client);
              }}
              variant="default"
              colorScheme="default"
              h={5}
              color="gray.700"
              aria-label="Settings"
              fontSize={20}
              icon={<RiSettings3Line />}
            />
          </HStack>
        )
      }
    ],
    [goToClientSettings, deleteDisclosure]
  );

  return (
    <Box>
      <HStack mt="-4.5rem" px={12} mb={8} justify="flex-end">
        <Button
          size="sm"
          onClick={onAddClientOpen}
          h={10}
          fontSize={15}
          minW={144}
        >
          Add New Client
        </Button>
        <AddClient isOpen={isAddClientOpen} onClose={onAddClientClose} />
      </HStack>

      <CrudList<Client>
        columns={columns}
        listId={EntityRouteListIds.ManageClients}
        searchFields={searchFields}
        useSimpleSearch
        searchOperator={ListSearchOperator.Or}
        onRowItemClick={goToClient}
      />
      <ConfirmModal
        {...deleteDisclosure}
        confirmText="Unlink"
        title="Confirmation: Unlink Client?"
        content={`Are you sure you want to unlink ${selectedClient?.name} from your client list? This action is irreversible and will remove all associated data. Please confirm your decision.`}
        confirmAction={() => handleUnlinkClient(selectedClient!)}
      />
    </Box>
  );
};
